import { Node, Edge } from 'reactflow';
import { ArchitectureNode, ArchitectureEdge } from '../types';

export type LayoutAlgorithm = 'hierarchical' | 'force-directed' | 'grid' | 'layered';

interface LayoutOptions {
  direction?: 'TB' | 'LR' | 'BT' | 'RL'; // Top-Bottom, Left-Right, Bottom-Top, Right-Left
  spacing?: { x: number; y: number };
}

// Simple hierarchical layout (top-down tree)
export const hierarchicalLayout = (
  nodes: Node[],
  edges: Edge[],
  options: LayoutOptions = {}
): Node[] => {
  const { direction = 'TB', spacing = { x: 200, y: 150 } } = options;
  
  // Build adjacency list
  const children = new Map<string, string[]>();
  const parents = new Map<string, string>();
  const roots: string[] = [];
  
  edges.forEach(edge => {
    if (!children.has(edge.source)) {
      children.set(edge.source, []);
    }
    children.get(edge.source)!.push(edge.target);
    parents.set(edge.target, edge.source);
  });
  
  // Find root nodes (nodes with no incoming edges)
  nodes.forEach(node => {
    if (!parents.has(node.id)) {
      roots.push(node.id);
    }
  });
  
  // If no roots, pick first node
  if (roots.length === 0 && nodes.length > 0) {
    roots.push(nodes[0].id);
  }
  
  const nodeMap = new Map(nodes.map(n => [n.id, { ...n, position: { ...n.position } }]));
  const visited = new Set<string>();
  
  const layoutNode = (nodeId: string, level: number, index: number, levelCount: number) => {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    
    const node = nodeMap.get(nodeId);
    if (!node) return;
    
    if (direction === 'TB' || direction === 'BT') {
      const x = (index - (levelCount - 1) / 2) * spacing.x;
      const y = level * spacing.y;
      node.position.x = x;
      node.position.y = direction === 'TB' ? y : -y;
    } else {
      const x = level * spacing.x;
      const y = (index - (levelCount - 1) / 2) * spacing.y;
      node.position.x = direction === 'LR' ? x : -x;
      node.position.y = y;
    }
    
    const nodeChildren = children.get(nodeId) || [];
    nodeChildren.forEach((childId, childIndex) => {
      layoutNode(childId, level + 1, childIndex, nodeChildren.length);
    });
  };
  
  // Layout each root tree
  roots.forEach((rootId, rootIndex) => {
    layoutNode(rootId, 0, rootIndex, roots.length);
  });
  
  // Center the layout
  const allX = Array.from(nodeMap.values()).map(n => n.position.x);
  const allY = Array.from(nodeMap.values()).map(n => n.position.y);
  const minX = Math.min(...allX);
  const minY = Math.min(...allY);
  const centerX = (Math.max(...allX) + minX) / 2;
  const centerY = (Math.max(...allY) + minY) / 2;
  
  nodeMap.forEach(node => {
    node.position.x -= centerX;
    node.position.y -= centerY;
  });
  
  return Array.from(nodeMap.values());
};

// Simple force-directed layout (physics simulation)
export const forceDirectedLayout = (
  nodes: Node[],
  edges: Edge[],
  options: LayoutOptions = {}
): Node[] => {
  const { spacing = { x: 200, y: 150 } } = options;
  const iterations = 100;
  const k = Math.sqrt((spacing.x * spacing.y) / nodes.length); // Spring constant
  
  const nodeMap = new Map(nodes.map(n => [n.id, { ...n, position: { ...n.position }, velocity: { x: 0, y: 0 } }]));
  
  // Initialize positions randomly if needed
  nodeMap.forEach(node => {
    if (node.position.x === 0 && node.position.y === 0) {
      node.position.x = (Math.random() - 0.5) * 500;
      node.position.y = (Math.random() - 0.5) * 500;
    }
  });
  
  for (let iter = 0; iter < iterations; iter++) {
    // Calculate repulsive forces (all nodes repel each other)
    nodeMap.forEach((node, nodeId) => {
      let fx = 0, fy = 0;
      
      nodeMap.forEach((otherNode, otherId) => {
        if (nodeId === otherId) return;
        
        const dx = node.position.x - otherNode.position.x;
        const dy = node.position.y - otherNode.position.y;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;
        
        // Repulsive force
        const force = (k * k) / distance;
        fx += (dx / distance) * force;
        fy += (dy / distance) * force;
      });
      
      node.velocity.x = (node.velocity.x + fx) * 0.9; // Damping
      node.velocity.y = (node.velocity.y + fy) * 0.9;
    });
    
    // Calculate attractive forces (connected nodes attract)
    edges.forEach(edge => {
      const source = nodeMap.get(edge.source);
      const target = nodeMap.get(edge.target);
      if (!source || !target) return;
      
      const dx = target.position.x - source.position.x;
      const dy = target.position.y - source.position.y;
      const distance = Math.sqrt(dx * dx + dy * dy) || 1;
      
      // Attractive force
      const force = (distance * distance) / k;
      const fx = (dx / distance) * force;
      const fy = (dy / distance) * force;
      
      source.velocity.x += fx * 0.1;
      source.velocity.y += fy * 0.1;
      target.velocity.x -= fx * 0.1;
      target.velocity.y -= fy * 0.1;
    });
    
    // Update positions
    nodeMap.forEach(node => {
      node.position.x += node.velocity.x * 0.1;
      node.position.y += node.velocity.y * 0.1;
      // Reduce velocity over time
      node.velocity.x *= 0.9;
      node.velocity.y *= 0.9;
    });
  }
  
  // Center the layout
  const allX = Array.from(nodeMap.values()).map(n => n.position.x);
  const allY = Array.from(nodeMap.values()).map(n => n.position.y);
  const centerX = (Math.max(...allX) + Math.min(...allX)) / 2;
  const centerY = (Math.max(...allY) + Math.min(...allY)) / 2;
  
  nodeMap.forEach(node => {
    node.position.x -= centerX;
    node.position.y -= centerY;
  });
  
  return Array.from(nodeMap.values()).map(({ velocity, ...node }) => node);
};

// Grid layout (regular grid arrangement)
export const gridLayout = (
  nodes: Node[],
  edges: Edge[],
  options: LayoutOptions = {}
): Node[] => {
  const { spacing = { x: 200, y: 150 } } = options;
  
  const cols = Math.ceil(Math.sqrt(nodes.length));
  const rows = Math.ceil(nodes.length / cols);
  
  return nodes.map((node, index) => {
    const col = index % cols;
    const row = Math.floor(index / cols);
    
    return {
      ...node,
      position: {
        x: (col - (cols - 1) / 2) * spacing.x,
        y: (row - (rows - 1) / 2) * spacing.y
      }
    };
  });
};

// Layered layout (Sugiyama-style, simplified)
export const layeredLayout = (
  nodes: Node[],
  edges: Edge[],
  options: LayoutOptions = {}
): Node[] => {
  const { direction = 'TB', spacing = { x: 200, y: 150 } } = options;
  
  // Build graph structure
  const inDegree = new Map<string, number>();
  const outEdges = new Map<string, string[]>();
  
  nodes.forEach(node => {
    inDegree.set(node.id, 0);
    outEdges.set(node.id, []);
  });
  
  edges.forEach(edge => {
    inDegree.set(edge.target, (inDegree.get(edge.target) || 0) + 1);
    if (!outEdges.has(edge.source)) {
      outEdges.set(edge.source, []);
    }
    outEdges.get(edge.source)!.push(edge.target);
  });
  
  // Assign layers using longest path
  const layer = new Map<string, number>();
  const queue: string[] = [];
  
  nodes.forEach(node => {
    if (inDegree.get(node.id) === 0) {
      layer.set(node.id, 0);
      queue.push(node.id);
    }
  });
  
  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentLayer = layer.get(current) || 0;
    
    outEdges.get(current)?.forEach(target => {
      const targetInDegree = (inDegree.get(target) || 0) - 1;
      inDegree.set(target, targetInDegree);
      
      if (targetInDegree === 0) {
        layer.set(target, currentLayer + 1);
        queue.push(target);
      } else {
        // Update layer if needed (longest path)
        const existingLayer = layer.get(target) || 0;
        if (currentLayer + 1 > existingLayer) {
          layer.set(target, currentLayer + 1);
        }
      }
    });
  }
  
  // Position nodes in layers
  const layerGroups = new Map<number, string[]>();
  layer.forEach((layerNum, nodeId) => {
    if (!layerGroups.has(layerNum)) {
      layerGroups.set(layerNum, []);
    }
    layerGroups.get(layerNum)!.push(nodeId);
  });
  
  const nodeMap = new Map(nodes.map(n => [n.id, { ...n, position: { ...n.position } }]));
  
  layerGroups.forEach((nodeIds, layerNum) => {
    nodeIds.forEach((nodeId, index) => {
      const node = nodeMap.get(nodeId);
      if (!node) return;
      
      const layerSize = nodeIds.length;
      if (direction === 'TB' || direction === 'BT') {
        const x = (index - (layerSize - 1) / 2) * spacing.x;
        const y = layerNum * spacing.y;
        node.position.x = x;
        node.position.y = direction === 'TB' ? y : -y;
      } else {
        const x = layerNum * spacing.x;
        const y = (index - (layerSize - 1) / 2) * spacing.y;
        node.position.x = direction === 'LR' ? x : -x;
        node.position.y = y;
      }
    });
  });
  
  // Center the layout
  const allX = Array.from(nodeMap.values()).map(n => n.position.x);
  const allY = Array.from(nodeMap.values()).map(n => n.position.y);
  const centerX = (Math.max(...allX) + Math.min(...allX)) / 2;
  const centerY = (Math.max(...allY) + Math.min(...allY)) / 2;
  
  nodeMap.forEach(node => {
    node.position.x -= centerX;
    node.position.y -= centerY;
  });
  
  return Array.from(nodeMap.values());
};

// Main layout function
export const applyLayout = (
  algorithm: LayoutAlgorithm,
  nodes: Node[],
  edges: Edge[],
  options?: LayoutOptions
): Node[] => {
  switch (algorithm) {
    case 'hierarchical':
      return hierarchicalLayout(nodes, edges, options);
    case 'force-directed':
      return forceDirectedLayout(nodes, edges, options);
    case 'grid':
      return gridLayout(nodes, edges, options);
    case 'layered':
      return layeredLayout(nodes, edges, options);
    default:
      return nodes;
  }
};
